import {useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {CheckboxValue} from "shared/ui";
import {useDataAll} from "shared/hooks/useDataAll";

type UseCsvCheckboxFilterParams<T> = {
  externalIsLoading?: boolean;
  externalListData?: T[];
  fetchParams?: Record<string, unknown>;
  queryKey: string;
  url?: string | null;
  value?: string | null;
  toOption: (item: T) => {
    label: string;
    value: string;
    className: string;
  };
  toValue: (item: T) => string;
};

export const useCsvCheckboxFilter = <T, >({
                                            fetchParams,
                                            externalIsLoading,
                                            externalListData,
                                            queryKey,
                                            url,
                                            value,
                                            toOption,
                                            toValue,
                                          }: UseCsvCheckboxFilterParams<T>) => {
  const [_, setSearchParams] = useSearchParams();
  const shouldUseExternalList = Array.isArray(externalListData);
  const {listData: fetchedListData, isLoading: fetchedIsLoading} = useDataAll<T>({
    url: shouldUseExternalList ? null : (url ?? null), params: fetchParams
  });
  const listData = shouldUseExternalList ? externalListData ?? [] : fetchedListData;
  const isLoading = shouldUseExternalList ? Boolean(externalIsLoading) : fetchedIsLoading;
  
  const allValues = useMemo(() => listData.map(toValue), [listData, toValue]);
  
  const selectedValues = useMemo(() => {
    if (!value) {
      return [];
    }
    
    return value.split(",")
                .filter(Boolean);
  }, [value]);
  
  const options = useMemo(() => {
    if (!listData.length) {
      return [];
    }
    
    return listData.map(toOption);
  }, [listData, toOption]);
  
  const checkAll = listData.length > 0 && listData.length === selectedValues.length;
  
  const setCsvValues = (values: string[]) => {
    setSearchParams((params) => {
      if (values.length === 0) {
        params.delete(queryKey);
        return params;
      }
      
      params.set(queryKey, values.join(","));
      return params;
    });
  };
  
  const handleChangeChecked = (checkedValues: CheckboxValue[]) => {
    setCsvValues(checkedValues as string[]);
  };
  
  const handleCheckAll = (checked: boolean) => {
    setCsvValues(checked ? allValues : []);
  };
  
  return {
    checkAll, handleChangeChecked, handleCheckAll, isLoading, options, selectedValues, listData,
  };
};
